/**
 * texture_tiers — pick the TEXTURE TIER file a client should download for a
 * (render_asset, lod) given its quality preset (settlement study R1, 2026-09-02).
 *
 * A tier is the same GLB with its KTX2 mip chains truncated at cook time so
 * no texture exceeds `maxTextureRes`. The texels are exactly the levels the
 * parse-time cap (`texture_budget.ts`) would keep; choosing the tier BEFORE
 * the fetch is what turns the potato city walk's 710 MB into the ~1/10 it
 * actually renders. The parse-time cap stays as the safety net.
 *
 * Rule: the largest tier whose `maxTextureRes` ≤ the preset cap; none when
 * no tier row is small enough (the canonical/variant file is then the only
 * option and the parse-time cap does its old job). The cooker registers the
 * source file itself at its own res (the SELF row), so a cap at or above the
 * source res resolves to the untiered file. Pure — the harness pins it.
 *
 * Opt out at runtime (A/B, a suspect device): localStorage
 * `atlas:texture-tiers` = 'off' or `?textureTiers=off`.
 */

export interface TextureTierRow {
  lod: number;
  maxTextureRes: number;
  uri: string;
  sizeBytes: bigint | number;
}

/** The tier to download for `lod` under `cap`, or undefined for the untiered file. */
export function pickTextureTier<T extends TextureTierRow>(rows: Iterable<T>, lod: number, cap: number): T | undefined {
  let best: T | undefined;
  for (const r of rows) {
    if (r.lod !== lod || r.maxTextureRes > cap) continue;
    if (!best || r.maxTextureRes > best.maxTextureRes) best = r;
  }
  return best;
}

let optOut: boolean | null = null;
/** True when texture tiers are switched off for this session. */
export function textureTiersOptedOut(): boolean {
  if (optOut !== null) return optOut;
  try {
    optOut = (typeof localStorage !== 'undefined' && localStorage.getItem('atlas:texture-tiers') === 'off')
      || (typeof location !== 'undefined' && /[?&]textureTiers=off\b/.test(location.search));
  } catch { optOut = false; }
  return optOut;
}

/** Test hook: reset the cached opt-out read. */
export function resetTextureTierOptOut(): void { optOut = null; }
